import {
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CommonService } from '../../../../../../shared/services/common.service';
import { CustomerSharedService } from '../../../../customer-shared.service';
import { DeviceService } from '../../device.service';

import * as XLSX from 'xlsx';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-storage-sensor-management',
  templateUrl: './storage-sensor-management.component.html',
  styleUrls: ['./sensor.component.css'],
})
export class StorageSensorManagementComponent implements OnInit {
  @ViewChild('addSensorFile', { static: false }) addSensorFile!: ElementRef;

  // Data lists
  sensorList$: any[] = [];
  filteredSensorList$: any[] = [];
  whitelistData: any[] = [];
  modelNumbers: any[] = [];
  deviceEUIs: any[] = [];
  filteredDeviceEUIs: any[] = [];
  locationOptions: any[] = [];
  unmappedExternalSensors: any = [];
  gatewaysByLocation: any = [];

  // File upload
  fileList: File[] = [];
  excelErr: string = '';
  excelErrLink: string = '';
  EXPECTED_HEADERS = [
    'sensor_name',
    'location_Id',
    'serial_no',
    'model_no',
    'reading_type',
    'deviceeui',
    'gateway_serial',
    'is_active',
    'last_update_user',
    'external_sensor_sr_no',
  ];
  isSuccessMessage: boolean = false;

  // Modal states
  isExcelModalVisible = false;
  isSensorModalVisible = false;
  isRequestExtSensorModalVisible = false;
  isConfirmLoading = false;
  isEditMode: boolean = false;
  editData: any = null;

  quickFilter: 'all' | 'active' | 'inActive' = 'all';
  counts = { total_count: 0, active_count: 0, inactive_count: 0 };
  searchText!: string;

  form!: FormGroup;
  requestExtSensorForm!: FormGroup;
  customer_id: string;
  apiCallLoaders = { isSensorsLoading: false };
  devicesForSelectedLocation: any[] = [];

  // User info
  admin_name = localStorage.getItem('admin_name') || '';
  user_id = localStorage.getItem('user_id') || '';

  // Table columns configuration
  listOfColumns: any[] = [
    { name: 'STATUS', key: 'status' },
    { name: 'NAME', key: 'sensor_name' },
    { name: 'DEVEUI', key: 'deviceeui' },
    { name: 'MODEL NO', key: 'model_number' },
    { name: 'CAPABILITIES', key: 'capabilities' },
    { name: 'EXT SENSOR', key: 'external_sensor' },
    { name: 'ASSOCIATED ASSET', key: 'asset_name' },
    { name: 'LOCATION', key: 'display_name' },
  ];

  constructor(
    public customerSharedService: CustomerSharedService,
    public route: ActivatedRoute,
    private fb: FormBuilder,
    public deviceService: DeviceService,
    public commonService: CommonService,
    public customerSharedSvc: CustomerSharedService,
    private ngZone: NgZone
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit() {
    this.initializeForm();
    this.loadInitialData();
  }

  initializeForm() {
    this.form = this.fb.group({
      sensor_name: [null, Validators.required],
      modelNo: [null, Validators.required],
      deveui: [null, Validators.required],
      serialNo: [null, Validators.required],
      capabilities: [null],
      extSensorId: [null],
      gatewayName: [null, Validators.required],
      location: [null, Validators.required],
    });

    this.requestExtSensorForm = this.fb.group({
      location: [null, Validators.required],
      deviceeuis: [[], Validators.required],
    });
  }

  loadInitialData() {
    this.getSensorsbyCustomerId();
    this.getWhitelistData();
    this.getUnmappedExternalSensors();
    this.getGatewaysByLocation();
    this.getLocationByCustomerId();
    this.filteredSensorList$ = [];
  }

  getSensorsbyCustomerId() {
    this.apiCallLoaders.isSensorsLoading = true;
    this.counts = { total_count: 0, active_count: 0, inactive_count: 0 };
    this.sensorList$ = [];

    this.ngZone.runOutsideAngular(() => {
      const storageSensors$ = this.deviceService
        .getSensorsByCustomerId(this.customer_id)
        .pipe(untilDestroyed(this));

      storageSensors$
        .toPromise()
        .then((storageResponse) => {
          if (
            storageResponse?.result &&
            (storageResponse?.status === 200 ||
              !storageResponse.hasOwnProperty('status'))
          ) {
            const sensorsArray = Array.isArray(storageResponse.result)
              ? storageResponse.result
              : [storageResponse.result];
            this.sensorList$ = sensorsArray.map((sensor: any) => ({
              ...sensor,
              sensor_type: 'storage',
            }));
          }

          this.sensorList$.forEach((sensor) => {
            this.counts.total_count++;
            if (sensor?.is_active === true) this.counts.active_count++;
            if (sensor?.is_active === false) this.counts.inactive_count++;
          });

          this.getSearchDetails('');
          this.filteredSensorList$ = [...this.sensorList$];

          this.ngZone.run(() => {
            this.apiCallLoaders.isSensorsLoading = false;
          });
        })
        .catch((error) => {
          console.error('Error fetching sensors:', error);
          this.ngZone.run(() => {
            this.apiCallLoaders.isSensorsLoading = false;
          });
        });
    });
  }

  getWhitelistData() {
    this.deviceService
      .getWhitelistData()
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        if (response && response.result) {
          this.whitelistData = response.result;
          this.modelNumbers = this.extractUniqueModelNumbers(this.whitelistData);
          this.deviceEUIs = this.whitelistData.filter(
            (item: any) => item.device_ui
          );
        }
      });
  }

  getUnmappedExternalSensors() {
    this.deviceService
      .getUnmappedExternalSensors()
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        if (response && response.result) {
          this.unmappedExternalSensors = response.result.map((ext: any) => ({
            external_sensor_sr_no: ext.external_sensor_sr_no,
            id: ext.id,
          }));
        }
      });
  }

  getGatewaysByLocation() {
    const obj = {
      group_id: this.customer_id,
    };
    this.deviceService
      .getGatewaysByLocation(obj)
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        if (response && response.data) {
          this.gatewaysByLocation = response.data;
        }
      });
  }

  getLocationByCustomerId() {
    this.commonService
      .getLocationList(Number(this.customer_id))
      .subscribe({
        next: (response: any) => {
          const locations = Array.isArray(response?.data)
            ? response.data
            : [];
          this.locationOptions = locations;
        },
        error: (err) => {
          console.error('Error fetching locations', err);
          this.commonService.message.error('Failed to fetch locations');
        },
      });
  }

  refreshHandler(): void {
    this.sensorList$ = [];
    this.filteredSensorList$ = [];
    this.quickFilter = 'all';
    this.searchText = '';
    this.getSensorsbyCustomerId();
  }

  filterData(): void {
    let tempList = [...this.sensorList$];

    if (this.quickFilter === 'active') {
      tempList = tempList.filter((sensor) => sensor.is_active);
    } else if (this.quickFilter === 'inActive') {
      tempList = tempList.filter((sensor) => !sensor.is_active);
    }

    this.filteredSensorList$ = tempList;
  }

  getSearchDetails(val: any): void {
    let searchText = (val || '').trim().toLowerCase();
    this.filteredSensorList$ = this.sensorList$.filter((sensor) => {
      if (!searchText) return true;

      return (
        (sensor.sensor_name &&
          sensor.sensor_name.toLowerCase().includes(searchText)) ||
        (sensor.deviceeui &&
          sensor.deviceeui.toLowerCase().includes(searchText)) ||
        (sensor.display_name &&
          sensor.display_name.toLowerCase().includes(searchText)) ||
        (sensor.model_number &&
          sensor.model_number.toLowerCase().includes(searchText))
      );
    });
  }

  // Modal and form handlers
  showModal(type: string): void {
    if (type === 'excel') {
      this.isExcelModalVisible = true;
    } else if (type === 'sensor') {
      this.isSensorModalVisible = true;
    } else if (type === 'requestExtSensor') {
      this.isRequestExtSensorModalVisible = true;
    }
  }

  handleCancel(type: string): void {
    if (type === 'excel') {
      this.isExcelModalVisible = false;
      this.fileList = [];
      this.resetExcelState();
    } else if (type === 'sensor') {
      this.isSensorModalVisible = false;
    } else if (type === 'requestExtSensor') {
      this.isRequestExtSensorModalVisible = false;
      this.requestExtSensorForm.reset();
    }
  }

  addEditAsset(type: string, data?: any) {
    this.isEditMode = type === 'edit';
    this.editData = data;

    if (this.isEditMode && data) {
      if (data?.external_sensor_id && data?.external_sensor_serial_no) {
        const externalSensorExists = this.unmappedExternalSensors.some(
          (sensor: any) => sensor.id === data.external_sensor_id
        );
        if (!externalSensorExists) {
          this.unmappedExternalSensors = [
            ...this.unmappedExternalSensors,
            {
              id: data.external_sensor_id,
              external_sensor_sr_no: data.external_sensor_serial_no,
            },
          ];
        }
      }

      this.form.patchValue({
        sensor_name: data?.sensor_name,
        modelNo: data?.model_number,
        deveui: data?.deviceeui,
        capabilities: data?.capabilities,
        extSensorId: data?.external_sensor_id,
        gatewayName: data?.gateway_serial,
        location: data?.location_id,
        serialNo: data?.serial_number,
      });
      this.showModal('sensor');
    } else {
      this.form.reset();
      this.showModal('sensor');
    }
  }

  onSubmit() {
    const currentForm: FormGroup = this.form;

    if (currentForm.invalid) {
      Object.keys(currentForm.controls).forEach((key) => {
        const control = currentForm.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const formData: any = {
      sensor_name: currentForm.value.sensor_name,
      model_number: currentForm.value.modelNo,
      deviceeui: currentForm.value.deveui,
      gateway_serial: currentForm.value.gatewayName,
      group_id: currentForm.value.location,
      external_sensor_id: currentForm.value.extSensorId || null,
      capabilities: currentForm.value.capabilities,
      serial_no: currentForm.value.serialNo,
      last_update_user: this.admin_name,
      user_id: this.user_id,
    };

    this.isConfirmLoading = true;

    if (this.isEditMode && this.editData?.idt_sensor) {
      formData['idt_sensor'] = this.editData.idt_sensor;
    }

    const apiCall = this.isEditMode
      ? this.deviceService.updateSensor(formData)
      : this.deviceService.addSensor(formData);

    apiCall.pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        if (response?.status === 200) {
          this.commonService.message.success(
            response?.data?.message ||
              (this.isEditMode
                ? 'Sensor updated successfully'
                : 'Sensor added successfully')
          );
          this.isSensorModalVisible = false;
          this.getSensorsbyCustomerId();
        } else {
          this.commonService.message.error(
            response?.message ||
              (this.isEditMode
                ? 'Failed to update sensor'
                : 'Failed to add sensor')
          );
        }
        this.isConfirmLoading = false;
      },
      error: (error: any) => {
        this.commonService.message.error(
          this.isEditMode
            ? 'An error occurred while updating the sensor'
            : 'An error occurred while adding the sensor'
        );
        console.error('Error:', error);
        this.isConfirmLoading = false;
      },
    });
  }

  deleteSensor(idt_sensor: string) {
    const payload = {
      sensorId: idt_sensor,
    };
    this.deviceService
      .deleteSensor(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response: any) => {
          if (response?.status === 200) {
            this.commonService.message.success('Sensor deleted successfully');
            this.getSensorsbyCustomerId();
          } else {
            this.commonService.message.error(response?.message);
          }
        },
        error: () => {
          this.commonService.message.error('Failed to delete sensor');
        },
      });
  }

  onFileChange(event: any): void {
    const file = event.target.files;
    const newFiles = Array.from(file);
    for (const f of newFiles as File[]) {
      const fileName = f.name.toLowerCase();
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const isValidType = validExtensions.some((ext) => fileName.endsWith(ext));
      if (!isValidType) {
        this.commonService.message.error(
          `File ${f.name} is not an allowed file type. Please upload excel file only.`
        );
        continue;
      }
      this.fileList.push(f);
    }
  }

  removeFile(index: number): void {
    this.fileList.splice(index, 1);
    if (this.addSensorFile) {
      this.addSensorFile.nativeElement.value = '';
    }
  }

  validateExcelFile(): boolean {
    if (this.fileList.length === 0) {
      this.commonService.message.error('Please select a file to upload');
      return false;
    }
    return true;
  }

  prepareExcelFormData(): FormData {
    const formData = new FormData();
    formData.append('xcelFile', this.fileList[0], this.fileList[0].name);
    formData.append('userId', this.user_id);
    return formData;
  }

  downloadTemplate() {
    const headers = this.EXPECTED_HEADERS;
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers]);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const sheetName = 'Sensor list';
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const fileName = 'Add Sensor Template.xlsx';
    XLSX.writeFile(wb, fileName);
  }

  onExcelSubmit() {
    if (!this.validateExcelFile()) {
      return;
    }
    this.resetExcelState();
    this.isConfirmLoading = true;

    const formData = this.prepareExcelFormData();

    const apiCall = this.deviceService.addSensorExcel(formData);

    apiCall.pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        this.handleExcelUploadResponse(response);
        this.isConfirmLoading = false;
      },
      error: (error: any) => {
        this.handleExcelUploadError(error);
        this.isConfirmLoading = false;
      },
    });
  }

  resetExcelState(): void {
    this.excelErr = '';
    this.excelErrLink = '';
    this.isSuccessMessage = false;
  }

  handleExcelUploadResponse(response: any): void {
    if (response?.status === 200) {
      if (response?.result) {
        this.excelErrLink = response.result;
      }
      this.isSuccessMessage = true;
      this.excelErr = 'Excel file processed successfully';

      if (!this.excelErrLink) {
        this.isExcelModalVisible = false;
        this.fileList = [];
      }
      this.getSensorsbyCustomerId();
    } else {
      this.isSuccessMessage = false;
      this.excelErr =
        response?.message ||
        response?.error_message ||
        'Failed to process excel file';
      if (response?.log_file_url) {
        this.excelErrLink = response.log_file_url;
      }
      this.commonService.message.error(this.excelErr);
    }
  }

  handleExcelUploadError(error: any): void {
    this.isSuccessMessage = false;
    this.excelErr =
      error?.error?.message ||
      'An error occurred while uploading the excel file';
    if (error?.error?.log_file_url) {
      this.excelErrLink = error.error.log_file_url;
    }
    this.commonService.message.error(this.excelErr);
    console.error('Error uploading excel:', error);
  }

  downloadLogFile() {
    if (this.excelErrLink) {
      window.open(this.excelErrLink, '_blank');
    }
  }

  extractUniqueModelNumbers(data: any[]): any[] {
    const modelMap = new Map();

    data.forEach((item) => {
      if (item.model_no && !modelMap.has(item.model_no)) {
        modelMap.set(item.model_no, {
          model_no: item.model_no,
          capabilities: item.reading_type,
        });
      }
    });

    return Array.from(modelMap.values());
  }

  onModelNoChange(modelNo: string) {
    const selectedModel = this.modelNumbers.find(
      (model) => model.model_no === modelNo
    );
    if (selectedModel) {
      this.form.patchValue({
        capabilities: selectedModel.capabilities,
        deveui: null,
      });
      this.filteredDeviceEUIs = this.deviceEUIs.filter(
        (device) => device.model_no === modelNo
      );
    }
  }

  onDeviceEUISearch(event: any) {
    const searchTerm = event.target.value.toLowerCase();
    const modelFiltered = this.deviceEUIs.filter(
      (device) => device.model_no === this.form.get('modelNo')?.value
    );

    if (searchTerm) {
      this.filteredDeviceEUIs = modelFiltered.filter(
        (device) =>
          device.device_ui &&
          device.device_ui.toLowerCase().includes(searchTerm)
      );
    } else {
      this.filteredDeviceEUIs = modelFiltered;
    }
  }

  selectDeviceEUI(device: any) {
    this.form.patchValue({
      deveui: device.device_ui,
    });

    const selectedDeviceData = this.whitelistData.find(
      (item) =>
        item.device_ui === device.device_ui &&
        item.model_no === this.form.get('modelNo')?.value
    );

    if (selectedDeviceData) {
      const serialNoValue = selectedDeviceData.serial_no || '';
      const capabilitiesValue =
        selectedDeviceData.reading_type != null
          ? Array.isArray(selectedDeviceData.reading_type)
            ? selectedDeviceData.reading_type.join(', ')
            : selectedDeviceData.reading_type
          : null;

      this.form.patchValue({ serialNo: serialNoValue });
      if (capabilitiesValue != null) {
        this.form.patchValue({ capabilities: capabilitiesValue });
      }
    } else {
      this.form.patchValue({ serialNo: '' });
    }

    this.filteredDeviceEUIs = [];
  }

  onRequestExtSensorSubmit() {
    if (this.requestExtSensorForm.invalid) {
      Object.keys(this.requestExtSensorForm.controls).forEach((key) => {
        const control = this.requestExtSensorForm.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const selectedEUIs: string[] =
      this.requestExtSensorForm.get('deviceeuis')?.value || [];
    if (!selectedEUIs.length) {
      this.commonService.message.warning(
        'Please select at least one device'
      );
      return;
    }

    this.isConfirmLoading = true;

    const payload = { devices: selectedEUIs };

    this.deviceService
      .requestExternalSensorData(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response: any) => {
          if (response?.status === 200) {
            this.commonService.message.success(
              `Downlink sent to ${selectedEUIs.length} device(s)`
            );
          } else {
            this.commonService.message.error(
              response?.message || 'Failed to send downlink command'
            );
          }
          this.isConfirmLoading = false;
          this.isRequestExtSensorModalVisible = false;
          this.requestExtSensorForm.reset();
          this.devicesForSelectedLocation = [];
          this.getSensorsbyCustomerId();
        },
        error: () => {
          this.isConfirmLoading = false;
          this.commonService.message.error(
            'Failed to send downlink command'
          );
        },
      });
  }

  onRequestLocationChange(locationId: any) {
    this.devicesForSelectedLocation = (this.sensorList$ || []).filter(
      (s: any) => s?.location_id === locationId
    );
    this.requestExtSensorForm.patchValue({ deviceeuis: [] });
  }

  removeExternalSensor() {
    this.commonService.modalSvc.confirm({
      nzTitle: 'Remove External Sensor',
      nzContent:
        'Are you sure you want to remove the external sensor from this sensor? This action will unlink the external sensor from the main sensor.',
      nzOkText: 'Yes, Remove',
      nzCancelText: 'Cancel',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => {
        this.form.patchValue({
          extSensorId: null,
        });
      },
    });
  }
}

